/* SourceCapabilityMap — read-side visual for a source doc: a connection
 * summary strip, then capability cards GROUPED BY operation_type (the
 * closed enum), each with a risk badge and origin chip. Read-only — the
 * JSON view stays the edit surface. */
import React, {
    useMemo,
    useState
} from 'react';
import {
    Copy,
    Globe,
    Loader2,
    Plug,
    Search
} from 'lucide-react';
import {
    notify
} from 'shared/api/notifier';
import {
    appEngineService,
    describeError
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import {
    OperationTypeBadge,
    OriginChip,
    RiskBadge
} from '../badges';
import type {
    Capability,
    SourceDoc
} from '../../api/types';
import './visuals.css';

/** Closed-enum display order; anything else lands after, alphabetically. */
const OP_ORDER = ['read_list', 'read_one', 'create', 'update', 'delete', 'action'];

function groupCaps(caps: Capability[]): Array<[string, Capability[]]> {
    const groups = new Map<string, Capability[]>();
    for (const c of caps) {
        const key = c.operation_type || 'unspecified';
        groups.set(key, [...(groups.get(key) || []), c]);
    }
    const rank = (k: string) => {
        const i = OP_ORDER.indexOf(k);
        return i === -1 ? OP_ORDER.length : i;
    };
    return Array.from(groups.entries())
        .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
}

/** A-0003: the EXPLICIT search-index provisioning action — shown only on a
 * redis source carrying an ft_search capability. Idempotent server-side, so
 * clicking twice is safe ("existed"). */
function EnsureSearchIndexButton({
    doc
}: {
    doc: SourceDoc;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [busy, setBusy] = useState(false);
    const srcId = doc.source?.id;
    const ftCaps = (doc.capabilities || []).filter(
        (c) => (c as any)?.invocation?.op === 'ft_search');
    if (doc.source?.type !== 'redis' || !srcId || ftCaps.length === 0) {
        return null;
    }
    const run = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await appEngineService.ensureSearchIndex(
                tenancy, srcId,
                ftCaps.length === 1 ? ftCaps[0].id : undefined);
            notify(res.created
                ? `Search index ${res.index} created (${(res.fields || []).length} fields)`
                : `Search index ${res.index} already exists`, 'success');
        } catch (err) {
            notify(describeError(err), 'danger');
        } finally {
            setBusy(false);
        }
    };
    return (
        <button className="aps-btn aps-btn-secondary aps-btn-sm"
            disabled={busy}
            title="FT.CREATE the RediSearch index behind the ft_search capability (idempotent)"
            onClick={run}>
            {busy ? <Loader2 size={13} className="aps-spin" /> : <Search size={13} />}
            Ensure search index
        </button>
    );
}

/** A-0004: the EXPLICIT bucket-CORS provisioning action — shown on any cos
 * source (a cos source is enough; no capability filter needed). Idempotent
 * server-side, so clicking twice is safe. */
function EnableBucketCorsButton({
    doc
}: {
    doc: SourceDoc;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [busy, setBusy] = useState(false);
    const srcId = doc.source?.id;
    if (doc.source?.type !== 'cos' || !srcId) {
        return null;
    }
    const run = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await appEngineService.ensureBucketCors(tenancy, srcId);
            notify(`Browser access enabled for bucket ${res.bucket}`, 'success');
        } catch (err) {
            notify(describeError(err), 'danger');
        } finally {
            setBusy(false);
        }
    };
    return (
        <button className="aps-btn aps-btn-secondary aps-btn-sm"
            disabled={busy}
            title="Set the bucket CORS so the browser can upload/preview files (idempotent)"
            onClick={run}>
            {busy ? <Loader2 size={13} className="aps-spin" /> : <Globe size={13} />}
            Enable browser access
        </button>
    );
}

export default function SourceCapabilityMap({
    docText
}: {
    docText: string;
}) {
    const parsed = useMemo(() => {
        try {
            return {
                doc: JSON.parse(docText) as SourceDoc,
                err: null as string | null
            };
        } catch (e: any) {
            return {
                doc: null,
                err: e?.message || 'invalid JSON'
            };
        }
    }, [docText]);

    const caps: Capability[] = useMemo(() => {
        const list = parsed.doc?.capabilities;
        return Array.isArray(list) ? list : [];
    }, [parsed.doc]);
    const groups = useMemo(() => groupCaps(caps), [caps]);
    const originMix = useMemo(() => {
        const mix: Record<string, number> = {};
        for (const c of caps) {
            const o = c.origin || 'ingested';
            mix[o] = (mix[o] || 0) + 1;
        }
        return Object.entries(mix);
    }, [caps]);

    if (parsed.err) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <span>Cannot draw — the JSON does not parse ({parsed.err}).
                    Fix it in the JSON view.</span>
            </div>
        );
    }

    const src = parsed.doc?.source || {};

    return (
        <div className="aps-srcv">
            <div className="aps-srcv-summary">
                <Plug size={15} color="var(--aps-primary)" />
                <span className="aps-srcv-summary-name">
                    {src.name || src.id || 'source'}
                </span>
                <span className="aps-badge aps-badge-indigo">{src.type || '?'}</span>
                <span className="aps-mono" style={{
                    fontSize: 'var(--aps-text-xs)',
                    color: 'var(--aps-text-muted)'
                }}>
                    {src.id || '—'} · {caps.length} capabilit{caps.length === 1 ? 'y' : 'ies'}
                </span>
                {originMix.map(([origin, n]) => (
                    <span key={origin} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4
                    }}>
                        <OriginChip origin={origin} />
                        <span className="aps-srcv-group-count">×{n}</span>
                    </span>
                ))}
                {parsed.doc && <EnsureSearchIndexButton doc={parsed.doc} />}
                {parsed.doc && <EnableBucketCorsButton doc={parsed.doc} />}
                {src.description && (
                    <p className="aps-srcv-summary-desc">{src.description}</p>
                )}
            </div>
            {caps.length === 0 && (
                <div className="aps-empty" style={{
                    padding: '30px 16px'
                }}>
                    <span>No capabilities in the current document.</span>
                </div>
            )}
            {groups.map(([op, list]) => (
                <div key={op} className="aps-srcv-group">
                    <div className="aps-srcv-group-head">
                        <OperationTypeBadge type={op} />
                        <span className="aps-srcv-group-count">
                            {list.length} capabilit{list.length === 1 ? 'y' : 'ies'}
                        </span>
                    </div>
                    <div className="aps-srcv-grid">
                        {list.map((c, i) => (
                            <div key={c.id || i} className="aps-srcv-card"
                                title={c.name || c.id}>
                                {/* NAME leads (every capability has one — the
                                    validator requires it); the id is the mono
                                    click-to-copy line below, so a minted
                                    cap_<hex> id no longer hides what a manual
                                    capability IS. */}
                                <span className="aps-srcv-card-name">
                                    {c.name || c.id || '(unnamed)'}
                                </span>
                                {c.id && (
                                    <button
                                        type="button"
                                        className="aps-srcv-card-id"
                                        title="Copy capability id"
                                        onClick={() => {
                                            if (navigator.clipboard) {
                                                navigator.clipboard.writeText(c.id)
                                                    .then(() => notify(`Copied ${c.id}`, 'success'))
                                                    .catch(() => undefined);
                                            }
                                        }}
                                    >
                                        {c.id}
                                        <Copy size={11} />
                                    </button>
                                )}
                                <p className="aps-srcv-card-desc">
                                    {c.description || '—'}
                                </p>
                                <span className="aps-srcv-card-chips">
                                    <RiskBadge level={c.risk_level} />
                                    <OriginChip origin={c.origin} />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
